import _ from 'lodash'
import Backbone from 'backbone'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'
import reportConstants from 'scoreboard/reports/report-constants'
import CellLayout from '../cell-layout'
import ReportRepeatBy from './designer-repeat-by'
import unwrapModel from 'scoreboard/components/unwrap-model'
import ReportColumn from './designer-column'
import * as ScorecardReportColumns from 'scoreboard/reports/scorecard-report/scorecard-report-columns'
const { isColumnScored } = ScorecardReportColumns

/**
 * Returns true if the object contains attributes that signal this column is
 * a calculated column with an equations
 * @param data
 * @return {undefined}
 */
function hasCalculatedEquation(data) {
	if (data instanceof Backbone.Model) {
		return hasCalculatedEquation(data.attributes)
	}
	return data.calculated && !_.isEmpty(data.equation || '')
}

/**
 * Given a layout, returns an array of all the equations found in the layout
 */
function equationsInLayout(layout) {
	return layout ? _(layout.data())
		.filter(data => hasCalculatedEquation(data))
		.map(data => data.equation)
		.value() : []
}

/**
 * A representation of a stack in a report.  This a logical model
 * meaning that it may be represented physically in a table by multiple columns,
 * but together they define the logical representation of a singular report
 * column.  This is client side model of the ReportStack class on the server
 */
class ReportStack extends Backbone.Model {

	preinitialize(attrs, opts = {}) {
		this.options = opts
	}

	initialize(attrs, opts = {}) {
		this.attributes.cid = attrs.cid || this.cid

		// bubble the events up from the column collection
		let columns = this.attributes.columns
		this.listenTo(columns, 'all', (...args) => {
			let eventName = args[0]

			// #54924 if there is a changeId event on one of the columns, that
			// should not trigger an event on the stack itself
			if (eventName === 'changeId') return

			if (eventName == 'update') {
				//if the last meta was removed from the report stack, remove
				//the stack itself
				if (this.attributes.columns.length == 0) {
					this.destroy()
					return   //don't bubble the update here, let the destroy bubble instead
				}
			}

			//if we're permanently added a new field, disable hiding headers if
			//only one field was previously showing
			let isHidingColumn = _.some(columns.models, m => m.get('hideLabel'))
			let nonTransientFields = _.filter(columns.models, m => !!m.get('transient') == false)
			if (isHidingColumn && nonTransientFields.length > 1) {
				_.each(nonTransientFields, m => m.unset('hideLabel', {silent: true}))
			}

			let opts = _.last(args)
			opts.stack = this
			opts.innerCollectionName = 'columns'
			this.trigger.apply(this, args)
		})

		//track the changes in equations in the layout
		this._previousCalculatedEquations = this._currentCalculatedEquations = []
		if (!_.isNil(this.attributes.advancedLayout)) {
			this._currentCalculatedEquations = equationsInLayout(this.attributes.advancedLayout)
		}
		this.on('change:advancedLayout', (model, layout, opts) => {
			this._previousCalculatedEquations = this._currentCalculatedEquations
			this._currentCalculatedEquations = equationsInLayout(layout)
		})

		this.on('change:repeatBy', (model, value, opts) => {
			this._addRepeatByListeners(value)
		})
	}

	_addRepeatByListeners(repeatByModel) {
		if (!repeatByModel) { return }
		repeatByModel.on('destroy', (model, value, opts) => {
			//check if this is the actual repeat by model, because child models
			//bubble their events up
			if (model !== repeatByModel) { return }

			//destroy the stack if it has more than one column and the repeat by
			//is being destroy (simple stacks can only have one column)
			let isRepeatByBeingDestroyedWithMultipleColumns = this.columns().size() > 1

			//if the repeat by requires a time range but the repeat by doesn't have
			//one, remove this stack
			let isRepeatByMissingRequiredTimeRange = this.requiresTimeRange()
				&& repeatByModel.timeRange() == null

			if (isRepeatByBeingDestroyedWithMultipleColumns || isRepeatByMissingRequiredTimeRange) {
				let hasTwoStacksOrLess = this.collection?.length < 3
				let isGrouping = _.find(this.collection?.models, stack => stack.hasGroupedColumn())
				if (isGrouping && hasTwoStacksOrLess) {
					//if we're grouping and this is the last ungrouped stack, do
					//not destroy it because we must have at least two stacks when grouped
					this.unset('repeatBy')
				} else {
					this.destroy()
				}
			} else {
				this.unset('repeatBy')
			}
		})
	}

	/**
	 * Adds a breakdown to this stack, the breakdown is appended to the top
	 * of the repeat-by structure.
	 *
	 * @param {Object|Model} breakdownByField the field data (or Backbone Model
	 * representing the breakdown) to breakdown by
	 */
	addBreakdown(breakdownByField) {
		let repeatBy = this.get('repeatBy')
		let breakdownBy = unwrapModel(breakdownByField)

		if (_.isNil(repeatBy)) {
			this.set('repeatBy', new ReportRepeatBy({
				headerOrder: [reportConstants.HEADER_TYPE_BREAKDOWN_ROW, reportConstants.HEADER_TYPE_FIELD_ROW],
				breakdowns: [breakdownBy]
			}, {parse: true}))
		} else {
			if (repeatBy.get('breakdowns').length > 1) {
				return	//do nothing we already have 2 breakdowns
			}
			repeatBy.addBreakdown(breakdownByField)
		}
	}

	validate(attrs = this.attributes, opts) {
		let errors = []
		let models = _.get(this.attributes.columns, 'models', [])
		_(models).filter(m => {
			return m.get('source') == reportConstants.SOURCE_CALCULATED && _.isNil(m.get('equation'))
		}).each(m => {
			errors.push({
				fieldIdOrColumnType: m.fieldIdOrColumnType(),
				message: i18n.get('report.designer.equationRequiredForColumn', i18n.get(m.get('label')))
			})
		})

		_.each(this.breakdowns(), breakdown => {
			let breakdownErrors = breakdown.validate()
			if (breakdownErrors) {
				errors.push({message: breakdownErrors})
			}
		})
		return !_.isEmpty(errors) ? errors : null
	}

	parse(attrs, opts) {
		let id = attrs.id

		if (!_.isArray(attrs.columns)) {
			attrs.columns = [attrs.columns]
		}
		let reportColumnOpts = {
			reportDomainType: opts.reportDomainType || this.options.reportDomainType
		}
		let columnModels = _.map(attrs.columns, cv => {
			return (cv instanceof ReportColumn) ? cv :
				new ReportColumn(cv, reportColumnOpts)
		})

		let columns = this.attributes.columns
		if (_.isNil(columns)) {
			columns = new Backbone.Collection(columnModels, opts)
		} else {
			columns.set(columnModels, _.extend({merge: true}, opts))
		}

		let repeatBy = attrs.repeatBy ? new ReportRepeatBy(attrs.repeatBy, {parse: true}) : null
		if (repeatBy) {
			this.stopListening(this.get('repeatBy'))
			this.listenTo(repeatBy, 'all', (...args) => {
				let opts = _.last(args)
				opts.stack = this
				opts.innerCollectionName = 'repeatBy'
				this.trigger.apply(this, args)
			})

			//https://github.com/SpiderStrategies/Scoreboard/issues/42669#issuecomment-983955794
			this.listenTo(repeatBy, 'change:sortByValue', (model, value, opts) => {
				if (opts.removeSort) {
					let models = _.get(this.attributes.columns, 'models', [])
					_.each(models, columnModel => {
						columnModel.unset('sortType', {silent: true})
					})
				}
			})
			this._addRepeatByListeners(repeatBy)
		}

		let advancedLayout = attrs.advancedLayout ? new CellLayout(attrs.advancedLayout) : null
		return { id, columns, repeatBy, advancedLayout }
	}

	findColumnsByFieldIdOrColumnType(fieldIdOrColumnType) {
		let property = ReportColumn.fieldIdOrColumnTypeProperty(this.options.reportDomainType)
		let attr = _.set({}, property, fieldIdOrColumnType)
		return this.attributes.columns.where(attr) || []
	}

	findColumnByAttribute(attr, opts = {}) {
		let columnModel = this.attributes.columns.findWhere(attr)
		if (opts.columnIndex && columnModel) {
			return this.findColumnIndex(columnModel)
		}
		return columnModel
	}

	findColumnIndex(columnModel) {
		return _.findIndex(this.attributes.columns.models, model => model === columnModel)
	}

	/**
	 * Returns the Backbone.Model representing this column if found in the collection
	 * of columns
	 * @param {Backbone.Model | Object} column  a Model or attribute of the column
	 * to find
	 */
	findColumn(column) {
		if (column instanceof Backbone.Model && column.isNew()) {
			//if new model, the id won't be populated so find by the attributes
			return this.findColumnByAttribute({cid: column.columnClientId()})
		}
		return this.attributes.columns.get(column)
	}

	toJSON() {
		let {repeatBy, advancedLayout} = this.attributes
		repeatBy = _.isEmpty(repeatBy) || _.isNil(repeatBy) ? null : repeatBy.toJSON()
		advancedLayout =  _.isNil(advancedLayout) ? null : repeatBy.toJSON()
		return {
			id: this.attributes.id,
			columns: this.attributes.columns.toJSON(),
			repeatBy,
			advancedLayout,
			cid: this.cid	//ensure we send the cid for unpersisted stacks
		}
	}

	clone() {
		let { reportDomainType } = this.options
		return new this.constructor(this.toJSON(), {parse: true, reportDomainType})
	}

	warnings() {
		if (this.isRepeatByStack()) {
			return this.get('repeatBy').warnings()
		}
		return []
	}

	/**
	 * Returns all the field ids (for dataset reports)/column types (non dataset
	 * reports) in columns collection in the simple/multi layout (not advanced
	 * layout) for this column.
	 *
	 * @param {Boolean} [opts.excludeTransient=true] if true only return handles
	 * that are not in transient columns
	 * @param {Boolean} [opts.includeRepeatBy=false] if true adds any handles
	 * that are used as a breakdown in a repeat by (if this stack has a repeat by)
	 * @param {Boolean} [opts.asSet=false] if true returns a set
	 */
	fieldIdsOrColumnTypes(opts = {excludeTransients: true}) {
		let models = this.attributes.columns.models
		if (opts.excludeTransients) {
			models = _.filter(models, m => !m.has('transient'))
		}
		let property = ReportColumn.fieldIdOrColumnTypeProperty(this.options.reportDomainType)
		//look for both, because this class is domain type agnostic. this should
		//not result in any issues since non-dataset and dataset report use completely
		//separate properties for reference underlying column data
		// #44773: Keys need to be strings in order to look up field select options.
		let idsOrTypes = _.map(models, m => `${m.get(property)}`)
		if (opts.includeRepeatBy && this.isRepeatByStack()) {
			idsOrTypes = idsOrTypes.concat(this.get('repeatBy').allBreakdownBys())
		}
		return opts.asSet ? new Set(idsOrTypes) : _.uniq(idsOrTypes)
	}

	/**
	 * Returns the Backbone Collection representing the collection of report columns
	 * shortcut for .get('columns')
	 */
	columns(index = null) {
		let collection =  this.attributes.columns
		return _.isFinite(index) ? collection.at(index) : collection
	}

	isRepeatByStack() {
		return this.has('repeatBy')
	}

	isRepeatByColumnStack() {
		return this.isRepeatByStack() &&
			_.some(this.get('repeatBy').get('breakdowns'),it =>
				it.isBreakingDownWithColumn())
	}

	isRepeatByRangeStack(reportModel) {
		return this.isRepeatByStack() &&
			_.some(this.get('repeatBy').get('breakdowns'),it =>
				it.isTimeBasedBreakdown(reportModel))
	}

	isAdvancedLayoutStack() {
		return this.has('advancedLayout')
	}

	hasGroupedColumn() {
		let columns = this.columns()
		return !this.isRepeatByStack() && columns.length == 1 && columns.at(0).isGrouping()
	}

	/**
	 * Returns an array of ReportBreakdown in the order the breakdowns appear
	 * if this is a repeat by stack, else an empty array
	 */
	breakdowns() {
		if (!this.isRepeatByStack()) {
			return []
		}
		return this.get('repeatBy').get('breakdowns')
	}

	//no-op because this is a nested model and all persistence is handle in root model
	sync() {}

	finalizeTransientColumn(reportModel, tableModel) {//make column permanent
		let transientColumn = this.findColumnByAttribute('transient')
		if (!transientColumn) { $.Deferred.resolve() }
		transientColumn.unset('newBlankColumn', {silent: true})

		//when a column is made permanent (non transient) check if we need to refetch
		let requiresFetch = this.requiresRefetch(reportModel, transientColumn)

		//silently if we are reloading with data
		transientColumn.unset('transient', {silent: requiresFetch})

		//indicate we need to spotlight this column
		this.spotlightIndex = this.findColumnIndex(transientColumn)

		//#39636 grab the data if necessary
		let def = requiresFetch ? reportModel.reload(): null
		return $.when(def).then(() => {
			tableModel.applySpotlight()
		})
	}

	removeTransientColumn() {
		if (this.isRepeatByStack()) {
			//check to see if the repeat has a transient breakdown,if so
			//destroy it
			let repeatBy = this.get('repeatBy')
			let breakdowns = repeatBy.get('breakdowns')
			let transientBreakdowns = _.filter(breakdowns, b => b.get('transient'))
			if (transientBreakdowns.length) {
				repeatBy.removeBreakdown(transientBreakdowns, {silent: true})
			}
		}
		let columns = this.get('columns')
		let columnsToRemove = columns.where({transient: true})
		columns.remove(columnsToRemove)
		if (columns.length == 0) {
			//only remove if all the columns in the stack are transient
			this.destroy()
		}
	}

	requiresTimeRange() {
		if (this.options.reportDomainType != 'scorecard-item') {
			return false
		}
		return _.some(this.attributes.columns.models, m => {
			return isColumnScored(m.get('columnType'))
		})
	}

	requiresRefetch(reportModel, column) {
		let columnsToCheck = column ? [column] : this.columns().models

		//we need to refetch if any meets one of the following
		// in a dataset report
		if (reportModel.reportDomainType == 'dataset') {
			return true
		}

		// in part of a repeat by stack
		if (this.isRepeatByStack()) {
			return true
		}

		// is in an aggregated report
		if (reportModel.isAggregating()) {
			return true
		}

		// has a calculated column
		if (!!_.find(columnsToCheck, c => c.get('calculated'))) {
			return true
		}

		// #42053 has a notes column
		if (!!_.find(columnsToCheck, c => c.get('columnType') === 'notes')) {
			return true
		}

		// a basic stack calculating against today's date
		if (_.find(columnsToCheck, c => {
			let columnType = c.get('columnType')
			return columnType == 'days-elapsed'
					|| columnType == 'percent-time-elapsed'
			})) {
			return true
		}

		return false
	}

}

export default ReportStack
export { hasCalculatedEquation }
ReportStack.__filename__ = 'reports/common/model/report-stack.js'
